import type { AccountPaymentMethod } from '@cocoonspace/sdk-js/types/accountPaymentMethod'
import { type ComponentProps, useMemo, useState } from 'react'
import { useGetAccountPaymentMethods } from '@cocoonspace/sdk-js/domains/account/account'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
} from '@cocoonspace/ui/components/dialog'
import {
	Select,
	SelectContent,
	SelectItem,
	SelectTrigger,
	SelectValue,
} from '@cocoonspace/ui/components/select'
import { Separator } from '@cocoonspace/ui/components/separator'
import { useLingui } from '@lingui/react/macro'
import {
	LandmarkIcon,
	PencilIcon,
	PlusIcon,
	TriangleAlertIcon,
} from 'lucide-react'

import { PaymentMethodCardIcon } from '~/domains/payments/components/payment-method-card-icon'
import { PaymentMethodsManager } from '~/domains/payments/components/templates/payment-methods-manager'

export const CardNumberAndExpired = ({ pm }: { pm: AccountPaymentMethod }) => {
	const isExpired = useMemo(() => {
		const { exp_year, exp_month } = pm.card ?? {}
		if (!exp_year || !exp_month) return false

		const today = new Date()
		const cardExpirationDate = new Date(`${exp_year}-${exp_month}-01`)

		return cardExpirationDate < today
	}, [pm.card])

	return (
		<div className='flex flex-row items-center gap-2 text-xs'>
			{`**** ${pm.card?.last_4}`}

			<div>
				{`${pm.card?.exp_month?.toString().padStart(2, '0')}/${pm.card?.exp_year}`}
			</div>

			{isExpired && <div className='text-red-500'>Expiré</div>}
		</div>
	)
}

const CardItem = ({ pm }: { pm: AccountPaymentMethod }) => (
	<div className='flex flex-row items-center gap-4'>
		<PaymentMethodCardIcon
			brand={pm.card?.brand}
			className='size-6'
		/>

		<div className='flex flex-col items-start'>
			<strong>{pm.title || pm.name}</strong>
			<CardNumberAndExpired pm={pm} />
		</div>
	</div>
)

const UpdateDialog = (props: ComponentProps<typeof Dialog>) => {
	const { t } = useLingui()

	return (
		<Dialog {...props}>
			<DialogContent className='w-full max-w-6xl overflow-auto bg-slate-100 max-md:z-[100] max-md:h-screen'>
				<DialogHeader className='text-left'>
					<DialogTitle>{t`Mes moyens de paiement`}</DialogTitle>
				</DialogHeader>

				<PaymentMethodsManager />
			</DialogContent>
		</Dialog>
	)
}

const UpdateBtn = ({ children, ...props }: ComponentProps<typeof Button>) => (
	<Button
		className='mx-auto flex p-2'
		size='sm'
		type='button'
		variant='link'
		{...props}
	>
		{children}
	</Button>
)

export const PaymentMethodSelectorContent = ({
	disabled,
	value,
	onChange,
}: {
	disabled?: boolean
	value?: string
	onChange: (value: string) => void
}) => {
	const { t } = useLingui()
	const [isUpdateDialogOpen, setIsUpdateDialogOpen] = useState(false)

	const { data: pms } = useGetAccountPaymentMethods({
		query: {
			select: (res) => res.data.filter((pm) => pm.type === 'card') || [],
		},
	})

	const defaultCard = pms?.find((pm) => pm.id === value)

	if (!defaultCard) {
		return (
			<>
				<div className='text-primary flex flex-row items-center justify-center gap-4 rounded border p-4 py-2'>
					<TriangleAlertIcon
						className='!size-6 shrink-0'
						strokeWidth={1.25}
					/>

					<div className='text-sm leading-tight'>{t`Vous n'avez pas de moyen de paiement.`}</div>

					<UpdateBtn onClick={() => setIsUpdateDialogOpen(true)}>
						<PlusIcon className='mr-1' />
						{t`Ajouter`}
					</UpdateBtn>
				</div>

				<UpdateDialog
					onOpenChange={setIsUpdateDialogOpen}
					open={isUpdateDialogOpen}
				/>
			</>
		)
	}

	return (
		<Select
			disabled={disabled}
			onValueChange={onChange}
			value={value}
		>
			<SelectTrigger className='h-[60px]'>
				<SelectValue />
			</SelectTrigger>

			<SelectContent>
				{pms?.map((pm) => (
					<SelectItem
						key={pm.id}
						value={pm.id!}
					>
						<CardItem pm={pm} />
					</SelectItem>
				))}

				<Separator />

				<UpdateBtn onClick={() => setIsUpdateDialogOpen(true)}>
					<PencilIcon />
					{t`Modifier`}
				</UpdateBtn>
			</SelectContent>

			<UpdateDialog
				onOpenChange={setIsUpdateDialogOpen}
				open={isUpdateDialogOpen}
			/>
		</Select>
	)
}

export const PaymentMethodSelector = (props: {
	disabled?: boolean
	value?: string
	onChange: (value: string) => void
}) => {
	const { t } = useLingui()

	const { data: isSepa, isLoading } = useGetAccountPaymentMethods({
		query: {
			select: (res) => res.data.find((pm) => pm.type === 'sepa_credit'),
		},
	})

	if (isLoading) return null

	if (isSepa) {
		return (
			<div
				className='flex flex-row items-center gap-4 rounded border p-4'
				data-testid='payment-method-selector-sepa'
			>
				<LandmarkIcon />
				<div className='flex flex-col'>{t`Virement bancaire`}</div>
			</div>
		)
	}

	return <PaymentMethodSelectorContent {...props} />
}
